// CustomLegend.jsx

/**
 * Get display title for comp map legend based on field/column name
 */
const getCompMapLegendTitle = (fieldName) => {
  const fieldTitleMap = {
    // Text fields
    'Comp Name': 'Comp Name',
    'CompName': 'Comp Name',
    'Submarket': 'Submarket',
    'Address': 'Address',
    'City': 'City',
    'Zip': 'Zip Code',
    'Latitude': 'Latitude',
    'Longitude': 'Longitude',
    'Management': 'Management',
    'Product Type': 'Product Type',
    'ProductType': 'Product Type',
    'Map Label': 'Map Label',
    'MapLabel': 'Map Label',
    // Numeric fields with descriptive titles
    'Total Units': 'Building Units',
    'TotalUnits': 'Building Units',
    'Occupancy': 'Occupancy',
    'Year Built': 'Year Built',
    'YearBuilt': 'Year Built',
    'Year Reno.': 'Year Renovated',
    'YearReno': 'Year Renovated',
    'Avg. Size': 'Average Unit Size (SF)',
    'AvgSize': 'Average Unit Size (SF)',
    'Avg. Base Rent': 'Average Base Rent',
    'AvgBaseRent': 'Average Base Rent',
    'Avg. Base PSF': 'Average Base Rent PSF',
    'AvgBasePSF': 'Average Base Rent PSF',
    'Avg. Conc.': 'Average Concession',
    'AvgConc': 'Average Concession',
    'Avg. Net Rent': 'Average Net Rent',
    'AvgNetRent': 'Average Net Rent',
    'Avg. Net PSF': 'Average Net Rent PSF',
    'AvgNetPSF': 'Average Net Rent PSF',
    // Percentage fields
    '% Studio': 'Percent Studio Units',
    '%Studio': 'Percent Studio Units',
    '% Open 1-Bed': 'Percent Open 1-Bed Units',
    '%Open1Bed': 'Percent Open 1-Bed Units',
    '% 1-Bed': 'Percent 1-Bed Units',
    '%1Bed': 'Percent 1-Bed Units',
    '% 2-Bed': 'Percent 2-Bed Units',
    '%2Bed': 'Percent 2-Bed Units',
    '% 3-Bed': 'Percent 3-Bed Units',
    '%3Bed': 'Percent 3-Bed Units',
    '% 4-Bed': 'Percent 4-Bed Units',
    '%4Bed': 'Percent 4-Bed Units',
    '% Other': 'Percent Other Units',
    '%Other': 'Percent Other Units',
    // Other numeric fields
    'Elevation': 'Elevation (Stories)',
    'Walk Score': 'Walk Score',
    'WalkScore': 'Walk Score'
  };

  return fieldTitleMap[fieldName] || fieldName;
};

const CustomLegend = ({ type, config }) => {
  if (!config) return null;

  // Debug logging - only in development and when explicitly needed
  // console.log('[CustomLegend] Received props:', { type, hasConfig: !!config });

  // For pipeline visualization (status-based)
  if (type === 'pipe') {
    const statusColors = config.statusColors || {};

    // Define the standard order for pipeline statuses
    const statusOrder = [
      "Under Construction", "Approved", "Pending", "Proposed",
      "Conceptual", "Stalled", "Inactive", "Pre-Leasing/Pre-Selling", "Complete"
    ];

    // Build ordered list: first the standard statuses in order, then any additional statuses
    const orderedStatuses = [];
    statusOrder.forEach(status => {
      if (statusColors[status] && status !== 'default') {
        orderedStatuses.push(status);
      }
    });

    // Add any remaining statuses that aren't in the standard order
    Object.keys(statusColors).forEach(status => {
      if (status !== 'default' && !statusOrder.includes(status)) {
        orderedStatuses.push(status);
      }
    });

    return (
      <div className="bg-white border border-gray-300 rounded-md shadow-md p-3 max-w-xs">
        <h3 className="text-sm font-semibold mb-2 text-black">
          {config.title || 'Pipeline Projects'}
        </h3>
        <div className="space-y-1.5">
          {orderedStatuses.map(status => (
            <div key={status} className="flex items-center">
              <div
                className="w-4 h-4 mr-2 rounded-full flex-shrink-0"
                style={{
                  backgroundColor: statusColors[status],
                  border: `${config.symbol?.outline?.width || 1}px solid ${config.symbol?.outline?.color || '#FFFFFF'}`
                }}
              />
              <span className="text-xs text-black truncate">{status}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }
  
  // For dot density visualization (geography-dotdensity)
  if (type === 'geography-dotdensity' || config.isDotDensity) {
    // If we have pre-built HTML, use it
    if (config.legendHtml) {
      return (
        <div
          className="bg-white border border-gray-300 rounded-md shadow-md max-w-xs"
          dangerouslySetInnerHTML={{ __html: config.legendHtml }}
        />
      );
    }

    // Otherwise build the legend from config
    const dotValue = config.dotValue || 1;
    const title = config.title || 'Dot Density';
    const isMultiVariable = config.isMultiVariable || (config.attributes && config.attributes.length > 1);
    // Default dot size: 9 for multi-variable (Comp/Pipe), 4 for single variable
    const dotSize = config.dotSize || 5;
    const attributes = config.attributes || [];

    // Helper function to convert color array to CSS color string
    const getColorString = (color, defaultColor = '#E60049') => {
      if (!color) return defaultColor;
      if (typeof color === 'string') return color;
      if (Array.isArray(color)) {
        if (color.length >= 3) {
          const [r, g, b, a = 1] = color;
          return `rgba(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)}, ${a})`;
        }
      }
      return defaultColor;
    };

    const displaySize = Math.max(dotSize * 2, 12);

    // Multi-variable dot density legend
    if (isMultiVariable && attributes.length > 1) {
      return (
        <div className="bg-white border border-gray-300 rounded-md shadow-md p-3 max-w-xs" style={{ backgroundColor: 'white' }}>
          <h3 className="text-sm font-semibold mb-2" style={{ color: '#000000' }}>
            {title}
          </h3>
          <p className="text-xs mb-2" style={{ color: '#666666' }}>
            1 dot = {dotValue.toLocaleString()}
          </p>
          <div className="space-y-1.5">
            {attributes.map((attr, index) => (
              <div key={index} className="flex items-center gap-2">
                <div
                  className="rounded-full flex-shrink-0"
                  style={{
                    width: `${displaySize}px`,
                    height: `${displaySize}px`,
                    backgroundColor: getColorString(attr.color)
                  }}
                />
                <span className="text-xs" style={{ color: '#000000' }}>
                  {attr.label || attr.field}
                </span>
              </div>
            ))}
          </div>
        </div>
      );
    }

    // Single variable dot density legend
    const dotColor = config.color || attributes?.[0]?.color || '#E60049';
    const label = attributes?.[0]?.label || `${dotValue.toLocaleString()} per dot`;
    const colorStr = getColorString(dotColor);

    return (
      <div className="bg-white border border-gray-300 rounded-md shadow-md p-3 max-w-xs" style={{ backgroundColor: 'white' }}>
        <h3 className="text-sm font-semibold mb-2" style={{ color: '#000000' }}>
          {title}
        </h3>
        <div className="flex items-center gap-2">
          <div
            className="rounded-full flex-shrink-0"
            style={{
              width: `${displaySize}px`,
              height: `${displaySize}px`,
              backgroundColor: colorStr
            }}
          />
          <span className="text-xs" style={{ color: '#000000' }}>
            {label}
          </span>
        </div>
      </div>
    );
  }

  // For geography heat map visualization
  if (type === 'geography' || type === 'geography-heatmap' || type === 'geography-value') {
    const classBreaks = config.classBreakInfos || [];
    const field = config.field || 'Value';

    // Helper function to convert color array to CSS color string
    const getColorString = (color, defaultColor = '#800080') => {
      if (!color) return defaultColor;
      if (typeof color === 'string') return color;
      if (Array.isArray(color)) {
        // Handle RGBA array format [r, g, b, a]
        if (color.length >= 3) {
          const [r, g, b, a = 1] = color;
          return `rgba(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)}, ${a})`;
        }
      }
      return defaultColor;
    };

    return (
      <div className="bg-white border border-gray-300 rounded-md shadow-md p-3 max-w-xs">
        <h3 className="text-sm font-semibold mb-2 text-black">
          {config.title || `${field} Heat Map`}
        </h3>
        <div className="space-y-1.5">
          {classBreaks.map((breakInfo, index) => (
            <div key={index} className="flex items-center">
              <div
                className="w-4 h-4 mr-2 flex-shrink-0"
                style={{
                  backgroundColor: getColorString(breakInfo.symbol?.color),
                  border: `${breakInfo.symbol?.outline?.width || 1}px solid ${getColorString(breakInfo.symbol?.outline?.color, '#FFFFFF')}`
                }}
              />
              <span className="text-xs text-black truncate">
                {breakInfo.label || (breakInfo.minValue === breakInfo.maxValue ? `${breakInfo.minValue}` : `${breakInfo.minValue} - ${breakInfo.maxValue === Infinity ? 'max' : breakInfo.maxValue}`)}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // For comp, 1varmap, and custom visualization (value-based breaks)
  if (type === 'comp' || type === '1varmap' || type === 'custom') {
    // For custom maps, use colorClassBreakInfos; for comp and 1varmap maps, use classBreakInfos
    const classBreaks = type === 'custom'
      ? (config.colorClassBreakInfos || config.classBreakInfos || [])
      : (config.classBreakInfos || []);
    const sizeBreaks = type === 'custom' ? (config.sizeClassBreakInfos || []) : [];
    const valueColumn = config.valueColumn || '';
    const valueColumn1 = config.valueColumn1 || '';
    const valueColumn2 = config.valueColumn2 || '';

    // Helper function to convert color array to CSS color string
    const getColorString = (color, defaultColor = '#800080') => {
      if (!color) return defaultColor;
      if (typeof color === 'string') return color;
      if (Array.isArray(color)) {
        // Handle RGBA array format [r, g, b, a]
        if (color.length >= 3) {
          const [r, g, b, a = 1] = color;
          return `rgba(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)}, ${a})`;
        }
      }
      return defaultColor;
    };

    // Simple legend for non-class-breaks mode
    if (classBreaks.length === 0) {
      const defaultTitle = type === 'comp' ? 'Comparable Properties' :
                          type === '1varmap' ? '1 Variable Map' :
                          '2 Variable Map';
      const defaultLabel = type === 'comp' ? 'Comparable Property' :
                          type === '1varmap' ? 'Data Point' :
                          'Data Point';
      return (
        <div className="bg-white border border-gray-300 rounded-md shadow-md p-3 max-w-xs">
          <h3 className="text-sm font-semibold mb-2 text-black">
            {config.title || `${valueColumn || defaultTitle}`}
          </h3>
          <div className="flex items-center">
            <div
              className="w-4 h-4 mr-2 rounded-full flex-shrink-0"
              style={{
                backgroundColor: getColorString(config.symbol?.color),
                border: `${config.symbol?.outline?.width || 1}px solid ${getColorString(config.symbol?.outline?.color, '#FFFFFF')}`
              }}
            />
            <span className="text-xs text-black">
              {config.legendInfo?.label || defaultLabel}
            </span>
          </div>
        </div>
      );
    }

    // For 2 Variable Maps with both color and size, show a compact combined legend
    if (type === 'custom' && valueColumn1 && valueColumn2 && sizeBreaks.length > 0) {
      // Use custom labels if available, otherwise fall back to column names
      const colorLabel = config.colorLabel || valueColumn1;
      const sizeLabel = config.sizeLabel || valueColumn2;

      console.log('[CustomLegend] Rendering 2-variable legend:', {
        colorLabel,
        sizeLabel,
        title: config.title,
        hasColorLabel: !!config.colorLabel,
        hasSizeLabel: !!config.sizeLabel,
        colorBreaksCount: classBreaks.length,
        sizeBreaksCount: sizeBreaks.length,
      });

      // Use custom legend section titles if provided, otherwise no title (user chose no label)
      const colorSectionTitle = config.colorLegendTitle || '';
      const sizeSectionTitle = config.sizeLegendTitle || '';

      return (
        <div className="bg-white border border-gray-300 rounded-md shadow-md p-3" style={{maxWidth: '320px'}}>
          {/* Color Section */}
          <div className="mb-3">
            {colorSectionTitle && (
              <h4 className="text-xs font-semibold mb-1.5 text-gray-700">
                {colorSectionTitle}
              </h4>
            )}
            <div className="space-y-1">
              {classBreaks.map((breakInfo, index) => (
                <div key={`color-${index}`} className="flex items-center">
                  <div
                    className="w-3 h-3 mr-1.5 rounded-full flex-shrink-0"
                    style={{
                      backgroundColor: getColorString(breakInfo.symbol?.color),
                      border: `1px solid ${getColorString(breakInfo.symbol?.outline?.color, '#FFFFFF')}`
                    }}
                  />
                  <span className="text-xs text-gray-700 truncate">
                    {breakInfo.label || (breakInfo.minValue === breakInfo.maxValue ? `${breakInfo.minValue}` : `${breakInfo.minValue} - ${breakInfo.maxValue === Infinity ? 'max' : breakInfo.maxValue}`)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Size Section */}
          <div className="pt-2 border-t border-gray-200">
            {sizeSectionTitle && (
              <h4 className="text-xs font-semibold mb-1.5 text-gray-700">
                {sizeSectionTitle}
              </h4>
            )}
            <div className="space-y-1">
              {sizeBreaks.map((sizeBreak, index) => (
                <div key={`size-${index}`} className="flex items-center">
                  {/* Wrapper maintains layout space, inner dot uses inverse scale to match map size */}
                  <div
                    className="mr-1.5 flex-shrink-0 flex items-center justify-center"
                    style={{
                      width: `${Math.round(sizeBreak.size)}px`,
                      height: `${Math.round(sizeBreak.size)}px`,
                    }}
                  >
                    <div
                      className="rounded-full"
                      style={{
                        width: `${Math.round(sizeBreak.size)}px`,
                        height: `${Math.round(sizeBreak.size)}px`,
                        backgroundColor: '#999',
                        border: '1px solid #666',
                        transform: 'scale(calc(1 / var(--legend-scale, 1)))',
                      }}
                    />
                  </div>
                  <span className="text-xs text-gray-700 truncate">
                    {sizeBreak.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    }

    // Standard legend for comp, 1varmap maps or single-variable custom maps
    let legendTitle;
    // Always prefer custom title if provided (check colorLegendTitle first for comp/1varmap, then title)
    if ((type === 'comp' || type === '1varmap') && config.colorLegendTitle) {
      legendTitle = config.colorLegendTitle;
    } else if (config.title) {
      legendTitle = config.title;
    } else if ((type === 'comp' || type === '1varmap') && valueColumn) {
      legendTitle = getCompMapLegendTitle(valueColumn);
    } else if (type === 'custom' && valueColumn1) {
      legendTitle = `Variable 1 (${valueColumn1}) - Color`;
    } else if (type === 'custom' && valueColumn) {
      legendTitle = `Variable 1 (${valueColumn}) - Color`;
    } else {
      legendTitle = valueColumn ? `${valueColumn} Values` : 'Data Distribution';
    }

    return (
      <div className="bg-white border border-gray-300 rounded-md shadow-md p-3 max-w-xs">
        <h3 className="text-sm font-semibold mb-2 text-black">
          {legendTitle}
        </h3>
        <div className="space-y-1.5">
          {classBreaks.map((breakInfo, index) => (
            <div key={index} className="flex items-center">
              <div
                className="w-4 h-4 mr-2 rounded-full flex-shrink-0"
                style={{
                  backgroundColor: getColorString(breakInfo.symbol?.color),
                  border: `${breakInfo.symbol?.outline?.width || 1}px solid ${getColorString(breakInfo.symbol?.outline?.color, '#FFFFFF')}`
                }}
              />
              <span className="text-xs text-black truncate">
                {breakInfo.label || (breakInfo.minValue === breakInfo.maxValue ? `${breakInfo.minValue}` : `${breakInfo.minValue} - ${breakInfo.maxValue === Infinity ? 'max' : breakInfo.maxValue}`)}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  }
  
  return null;
};

export default CustomLegend;